import jsfeat from 'jsfeat';
import { useEffect, useRef, useState } from 'react';
import {
  type DetectResult,
  type GrayMatrix,
  type TemplateData,
} from "../models/battleDetect";
import {
  FRAME_H,
  FRAME_W,
  FULL_FRAME_CROP_RECT,
  drawVideoFrame,
  type Rect,
} from './videoCrop';
import { callOpponentImageApi } from '../services/teamSearchService';

// 原来的固定区域为 { x: 114, y: 929, w: 38, h: 34 }
// 我们提取一个更大的搜索区域，以便在这个区域内进行滑动窗口匹配，容忍各种宽高比、黑边导致的偏移
const TEMPLATE_SEARCH_REGION = { x: 80, y: 900, w: 120, h: 80 };
const TEMPLATE_W = 38;
const TEMPLATE_H = 34;
const TEMPLATE_MATCH_THRESHOLD = 0.55;
const TEMPLATE_SRC = `${process.env.PUBLIC_URL ?? ''}/template.png`;
const SOURCE_TEMPLATE_REGION = { x: 1585, y: 927, w: 38, h: 118 };
const SOURCE_TEMPLATE_MATCH_THRESHOLD = 0.8;
const SOURCE_TEMPLATE_REQUIRED_FRAMES = 2;
const SOURCE_TEMPLATE_SRC = `${process.env.PUBLIC_URL ?? ''}/source_template.png`;
const OPPONENT_OVERLAY_SOURCE_REGION = { x: 1557, y: 154, w: 297, h: 745 };
const OPPONENT_COMPOSITE_REGION = { x: 1121, y: 172, w: 796, h: 870 };
const OPPONENT_OVERLAY_PADDING = 2;
const ALLY_STATUS_REGIONS = [
  { x: 540, y: 984, w: 280, h: 50 },
  { x: 8, y: 927, w: 280, h: 118 },
];
const OPPONENT_STATUS_REGIONS = [
  { x: 1100, y: 984, w: 280, h: 50 },
  { x: 1628, y: 927, w: 280, h: 118 },
];
let templateImagePromise: Promise<HTMLImageElement> | null = null;
let sourceTemplateImagePromise: Promise<HTMLImageElement> | null = null;

const DEFAULT_RESULT: DetectResult = {
  detectionTick: 0,
  templateScorePermille: 0,
  templateScorePercent: 0,
  templateDetected: false,
  sourceTemplateScorePermille: 0,
  sourceTemplateScorePercent: 0,
  sourceTemplateCheckActive: false,
  sourceTemplateDetected: false,
  sourceTemplateConsecutiveMatches: 0,
  allyStatusImageUrl: null,
  opponentStatusImageUrl: null,
  debug: {
    avgR: 0,
    avgG: 0,
    avgB: 0,
    regionHeight: 0,
    templateThresholdPercent: Math.round(TEMPLATE_MATCH_THRESHOLD * 100),
    templateThresholdScore: TEMPLATE_MATCH_THRESHOLD,
    templateRegion: { x: 114, y: 929, w: TEMPLATE_W, h: TEMPLATE_H },
    sourceTemplateThresholdPercent: Math.round(SOURCE_TEMPLATE_MATCH_THRESHOLD * 100),
    sourceTemplateThresholdScore: SOURCE_TEMPLATE_MATCH_THRESHOLD,
    sourceTemplateRegion: SOURCE_TEMPLATE_REGION,
    templateLogs: [],
  },
};

function loadTemplateImage(): Promise<HTMLImageElement> {
  if (templateImagePromise) {
    return templateImagePromise;
  }

  templateImagePromise = new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`模板图片加载失败: ${TEMPLATE_SRC}`));
    image.src = TEMPLATE_SRC;
  });

  return templateImagePromise;
}

function loadSourceTemplateImage(): Promise<HTMLImageElement> {
  if (sourceTemplateImagePromise) {
    return sourceTemplateImagePromise;
  }

  sourceTemplateImagePromise = new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`来源模板图片加载失败: ${SOURCE_TEMPLATE_SRC}`));
    image.src = SOURCE_TEMPLATE_SRC;
  });

  return sourceTemplateImagePromise;
}

function createGrayMatrix(width: number, height: number): GrayMatrix {
  return new jsfeat.matrix_t(width, height, jsfeat.U8_t | jsfeat.C1_t) as GrayMatrix;
}

function imageDataToGrayMatrix(imageData: ImageData): GrayMatrix {
  const gray = createGrayMatrix(imageData.width, imageData.height);
  jsfeat.imgproc.grayscale(
    imageData.data,
    imageData.width,
    imageData.height,
    gray,
    jsfeat.COLOR_RGBA2GRAY
  );
  return gray;
}

function calculateSobelMagnitude(
  gray: ArrayLike<number>,
  width: number,
  height: number
): Float32Array {
  const magnitude = new Float32Array(width * height);

  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const idx = y * width + x;
      const p00 = gray[idx - width - 1];
      const p01 = gray[idx - width];
      const p02 = gray[idx - width + 1];
      const p10 = gray[idx - 1];
      const p12 = gray[idx + 1];
      const p20 = gray[idx + width - 1];
      const p21 = gray[idx + width];
      const p22 = gray[idx + width + 1];

      const gx = -p00 + p02 - 2 * p10 + 2 * p12 - p20 + p22;
      const gy = -p00 - 2 * p01 - p02 + p20 + 2 * p21 + p22;
      magnitude[idx] = Math.min(255, Math.hypot(gx, gy) / 4);
    }
  }

  return magnitude;
}

function createTemplateEdgeMask(
  edge: ArrayLike<number>,
  alphaMask: ArrayLike<number>
): { edgeMask: Uint8Array; maskPixels: number } {
  const length = Math.min(edge.length, alphaMask.length);
  const edgeMask = new Uint8Array(length);
  let maxEdge = 0;

  for (let i = 0; i < length; i += 1) {
    if (!alphaMask[i]) continue;
    if (edge[i] > maxEdge) {
      maxEdge = edge[i];
    }
  }

  const threshold = Math.max(12, maxEdge * 0.3);
  let maskPixels = 0;

  for (let i = 0; i < length; i += 1) {
    const isActive = alphaMask[i] && edge[i] >= threshold;
    edgeMask[i] = isActive ? 1 : 0;
    if (isActive) {
      maskPixels += 1;
    }
  }

  if (maskPixels >= 12) {
    return { edgeMask, maskPixels };
  }

  maskPixels = 0;
  for (let i = 0; i < length; i += 1) {
    edgeMask[i] = alphaMask[i] ? 1 : 0;
    if (edgeMask[i]) {
      maskPixels += 1;
    }
  }

  return { edgeMask, maskPixels };
}

function imageToTemplateData(
  image: HTMLImageElement,
  width = TEMPLATE_W,
  height = TEMPLATE_H
): TemplateData {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });

  if (!ctx) {
    throw new Error('模板画布上下文创建失败');
  }

  ctx.drawImage(image, 0, 0, width, height);
  const imageData = ctx.getImageData(0, 0, width, height);
  const alphaMask = new Uint8Array(width * height);

  for (let i = 0; i < alphaMask.length; i += 1) {
    alphaMask[i] = imageData.data[i * 4 + 3] > 0 ? 1 : 0;
  }

  const gray = imageDataToGrayMatrix(imageData);
  const edge = calculateSobelMagnitude(gray.data, width, height);
  const { edgeMask, maskPixels } = createTemplateEdgeMask(edge, alphaMask);

  return {
    rgba: new Uint8ClampedArray(imageData.data),
    gray,
    alphaMask,
    edge,
    edgeMask,
    maskPixels,
  };
}

function calculateMaskedNormalizedCorrelation(
  source: ArrayLike<number>,
  template: ArrayLike<number>,
  mask: ArrayLike<number>
): number {
  const length = Math.min(source.length, template.length, mask.length);
  let sourceMean = 0;
  let templateMean = 0;
  let count = 0;

  for (let i = 0; i < length; i += 1) {
    if (!mask[i]) continue;
    sourceMean += source[i];
    templateMean += template[i];
    count += 1;
  }

  if (count < 2) {
    return 0;
  }

  sourceMean /= count;
  templateMean /= count;

  let numerator = 0;
  let sourceEnergy = 0;
  let templateEnergy = 0;

  for (let i = 0; i < length; i += 1) {
    if (!mask[i]) continue;
    const sourceDiff = source[i] - sourceMean;
    const templateDiff = template[i] - templateMean;
    numerator += sourceDiff * templateDiff;
    sourceEnergy += sourceDiff * sourceDiff;
    templateEnergy += templateDiff * templateDiff;
  }

  if (sourceEnergy < 1e-6 || templateEnergy < 1e-6) {
    return 0;
  }

  const score = numerator / Math.sqrt(sourceEnergy * templateEnergy);
  return Math.max(0, Math.min(1, score));
}

function calculateNormalizedCorrelation(
  source: ArrayLike<number>,
  template: ArrayLike<number>
): number {
  const length = Math.min(source.length, template.length);
  let sourceMean = 0;
  let templateMean = 0;

  for (let i = 0; i < length; i += 1) {
    sourceMean += source[i];
    templateMean += template[i];
  }

  if (length < 2) {
    return 0;
  }

  sourceMean /= length;
  templateMean /= length;

  let numerator = 0;
  let sourceEnergy = 0;
  let templateEnergy = 0;

  for (let i = 0; i < length; i += 1) {
    const sourceDiff = source[i] - sourceMean;
    const templateDiff = template[i] - templateMean;
    numerator += sourceDiff * templateDiff;
    sourceEnergy += sourceDiff * sourceDiff;
    templateEnergy += templateDiff * templateDiff;
  }

  if (sourceEnergy < 1e-6 || templateEnergy < 1e-6) {
    return 0;
  }

  const score = numerator / Math.sqrt(sourceEnergy * templateEnergy);
  return Math.max(0, Math.min(1, score));
}

function calculateMaskedGrayDifferenceSimilarity(
  source: ArrayLike<number>,
  template: ArrayLike<number>,
  mask: ArrayLike<number>
): number {
  const length = Math.min(source.length, template.length, mask.length);
  let totalDifference = 0;
  let count = 0;

  for (let i = 0; i < length; i += 1) {
    if (!mask[i]) continue;
    totalDifference += Math.abs(source[i] - template[i]);
    count += 1;
  }

  if (count === 0) {
    return 0;
  }

  return Math.max(0, Math.min(1, 1 - totalDifference / (count * 255)));
}

function calculateMaskedColorDifferenceSimilarity(
  source: Uint8ClampedArray,
  template: Uint8ClampedArray,
  mask: ArrayLike<number>
): number {
  const length = Math.min(source.length, template.length, mask.length * 4);
  const pixelLength = Math.floor(length / 4);
  let totalDifference = 0;
  let count = 0;

  for (let i = 0; i < pixelLength; i += 1) {
    if (!mask[i]) continue;
    const offset = i * 4;
    totalDifference += Math.abs(source[offset] - template[offset]);
    totalDifference += Math.abs(source[offset + 1] - template[offset + 1]);
    totalDifference += Math.abs(source[offset + 2] - template[offset + 2]);
    count += 3;
  }

  if (count === 0) {
    return 0;
  }

  return Math.max(0, Math.min(1, 1 - totalDifference / (count * 255)));
}

function calculateProjectionProfileSimilarity(
  source: ArrayLike<number>,
  template: ArrayLike<number>,
  width: number,
  height: number
): number {
  const sourceRows = new Float32Array(height);
  const templateRows = new Float32Array(height);
  const sourceCols = new Float32Array(width);
  const templateCols = new Float32Array(width);

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = y * width + x;
      sourceRows[y] += source[index];
      templateRows[y] += template[index];
      sourceCols[x] += source[index];
      templateCols[x] += template[index];
    }
  }

  for (let y = 0; y < height; y += 1) {
    sourceRows[y] /= width;
    templateRows[y] /= width;
  }

  for (let x = 0; x < width; x += 1) {
    sourceCols[x] /= height;
    templateCols[x] /= height;
  }

  const rowScore = calculateNormalizedCorrelation(sourceRows, templateRows);
  const colScore = calculateNormalizedCorrelation(sourceCols, templateCols);
  return rowScore * 0.65 + colScore * 0.35;
}

function calculateTemplateScore(
  imageData: ImageData,
  templateData: TemplateData,
): { score: number; grayScore: number; edgeScore: number; similarPixels: number } {
  const sourceGray = imageDataToGrayMatrix(imageData);
  const sourceEdge = calculateSobelMagnitude(
    sourceGray.data,
    imageData.width,
    imageData.height
  );
  const grayScore = calculateMaskedNormalizedCorrelation(
    sourceGray.data,
    templateData.gray.data,
    templateData.alphaMask
  );
  const edgeScore = calculateMaskedNormalizedCorrelation(
    sourceEdge,
    templateData.edge,
    templateData.edgeMask
  );
  const grayDifferenceScore = calculateMaskedGrayDifferenceSimilarity(
    sourceGray.data,
    templateData.gray.data,
    templateData.alphaMask
  );
  const colorDifferenceScore = calculateMaskedColorDifferenceSimilarity(
    imageData.data,
    templateData.rgba,
    templateData.alphaMask
  );
  const correlationScore = grayScore * 0.5 + edgeScore * 0.5;
  const differenceScore = colorDifferenceScore * 0.7 + grayDifferenceScore * 0.3;
  const profileScore = calculateProjectionProfileSimilarity(
    sourceGray.data,
    templateData.gray.data,
    imageData.width,
    imageData.height
  );
  const structuredDifferenceScore = differenceScore * 0.55 + profileScore * 0.45;

  return {
    score: Math.max(correlationScore, structuredDifferenceScore),
    grayScore,
    edgeScore,
    similarPixels: countSimilarPixels(
      sourceEdge,
      templateData.edge,
      templateData.edgeMask
    ),
  };
}

function countSimilarPixels(
  source: ArrayLike<number>,
  template: ArrayLike<number>,
  alphaMask: ArrayLike<number>,
  tolerance = 24
): number {
  const length = Math.min(source.length, template.length);
  let matchedPixels = 0;

  for (let i = 0; i < length; i += 1) {
    if (!alphaMask[i]) {
      continue;
    }
    if (Math.abs(source[i] - template[i]) <= tolerance) {
      matchedPixels += 1;
    }
  }

  return matchedPixels;
}

function summarizeImageData(imageData: ImageData): {
  avgR: number;
  avgG: number;
  avgB: number;
} {
  const { data } = imageData;
  let pixelCount = 0;
  let sumR = 0;
  let sumG = 0;
  let sumB = 0;

  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < 20) continue;
    sumR += data[i];
    sumG += data[i + 1];
    sumB += data[i + 2];
    pixelCount += 1;
  }

  if (pixelCount === 0) {
    return { avgR: 0, avgG: 0, avgB: 0 };
  }

  return {
    avgR: Math.round(sumR / pixelCount),
    avgG: Math.round(sumG / pixelCount),
    avgB: Math.round(sumB / pixelCount),
  };
}

function captureStackedRegions(
  frameCanvas: HTMLCanvasElement,
  regions: Array<{ x: number; y: number; w: number; h: number }>,
): string | null {
  const width = Math.max(...regions.map((region) => region.w));
  const height = regions.reduce((sum, region) => sum + region.h, 0);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return null;
  }

  let offsetY = 0;
  for (const region of regions) {
    ctx.drawImage(
      frameCanvas,
      region.x,
      region.y,
      region.w,
      region.h,
      0,
      offsetY,
      region.w,
      region.h,
    );
    offsetY += region.h;
  }

  return canvas.toDataURL('image/png');
}

function captureRegionCanvas(
  frameCanvas: HTMLCanvasElement,
  region: { x: number; y: number; w: number; h: number },
): HTMLCanvasElement | null {
  const canvas = document.createElement('canvas');
  canvas.width = region.w;
  canvas.height = region.h;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return null;
  }

  ctx.drawImage(
    frameCanvas,
    region.x,
    region.y,
    region.w,
    region.h,
    0,
    0,
    region.w,
    region.h,
  );

  return canvas;
}

function stripDataUrlPrefix(dataUrl: string): string {
  const commaIndex = dataUrl.indexOf(',');
  return commaIndex >= 0 ? dataUrl.slice(commaIndex + 1) : dataUrl;
}

function createOpponentCompositeJpgBase64(
  frameCanvas: HTMLCanvasElement,
  overlayCanvas: HTMLCanvasElement,
): string | null {
  const canvas = captureRegionCanvas(frameCanvas, OPPONENT_COMPOSITE_REGION);
  const ctx = canvas?.getContext('2d');

  if (!canvas || !ctx) {
    return null;
  }

  ctx.drawImage(
    overlayCanvas,
    canvas.width - overlayCanvas.width - OPPONENT_OVERLAY_PADDING,
    OPPONENT_OVERLAY_PADDING,
  );

  return stripDataUrlPrefix(canvas.toDataURL('image/jpeg', 0.92));
}

export function useBattleColorDetect(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  stream: MediaStream | null,
  options: { fps?: number; cropRect?: Rect; sourceTemplateActivationToken?: number } = {}
): DetectResult {
  const {
    fps = 5,
    cropRect = FULL_FRAME_CROP_RECT,
    sourceTemplateActivationToken = 0,
  } = options;
  const [result, setResult] = useState<DetectResult>(DEFAULT_RESULT);
  const timerRef = useRef<number | null>(null);
  const lastSignatureRef = useRef<string>('');
  const detectionTickRef = useRef(0);
  const lastSourceTemplateActivationTokenRef = useRef(0);

  useEffect(() => {
    if (!stream) {
      setResult(DEFAULT_RESULT);
      return;
    }

    let disposed = false;
    let detecting = false;
    let templateData: TemplateData | null = null;
    let sourceTemplateData: TemplateData | null = null;
    let sourceTemplateArmed = false;
    let sourceTemplateConsecutiveMatches = 0;
    let sourceTemplateScore = 0;
    let allyStatusImageUrl: string | null = null;
    let opponentStatusImageUrl: string | null = null;
    let opponentOverlayCanvas: HTMLCanvasElement | null = null;

    const frameCanvas = document.createElement('canvas');
    frameCanvas.width = FRAME_W;
    frameCanvas.height = FRAME_H;

    const frameCtx = frameCanvas.getContext('2d', { willReadFrequently: true });
    const canvas = document.createElement('canvas');
    canvas.width = TEMPLATE_SEARCH_REGION.w;
    canvas.height = TEMPLATE_SEARCH_REGION.h;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx || !frameCtx) {
      setResult(DEFAULT_RESULT);
      return;
    }

    const cleanupTimer = () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const detect = () => {
      if (detecting) return;

      const video = videoRef.current;
      if (!video) return;
      if (video.readyState < 2) return;
      if (video.videoWidth === 0 || video.videoHeight === 0) return;
      if (!templateData || !sourceTemplateData) return;

      detecting = true;

      try {
        drawVideoFrame(frameCtx, video, FRAME_W, FRAME_H, cropRect);
        ctx.clearRect(0, 0, TEMPLATE_SEARCH_REGION.w, TEMPLATE_SEARCH_REGION.h);
        ctx.drawImage(
          frameCtx.canvas,
          TEMPLATE_SEARCH_REGION.x,
          TEMPLATE_SEARCH_REGION.y,
          TEMPLATE_SEARCH_REGION.w,
          TEMPLATE_SEARCH_REGION.h,
          0,
          0,
          TEMPLATE_SEARCH_REGION.w,
          TEMPLATE_SEARCH_REGION.h,
        );
        const region = ctx.getImageData(0, 0, TEMPLATE_SEARCH_REGION.w, TEMPLATE_SEARCH_REGION.h);
        const regionSummary = summarizeImageData(region);
        const sourceGray = imageDataToGrayMatrix(region);
        const sourceEdge = calculateSobelMagnitude(
          sourceGray.data,
          TEMPLATE_SEARCH_REGION.w,
          TEMPLATE_SEARCH_REGION.h
        );
        
        let bestScore = -1;
        let bestGrayScore = 0;
        let bestEdgeScore = 0;
        let bestSimilarPixels = 0;
        let bestX = 0;
        let bestY = 0;

        for (let dy = 0; dy <= TEMPLATE_SEARCH_REGION.h - TEMPLATE_H; dy += 1) {
          for (let dx = 0; dx <= TEMPLATE_SEARCH_REGION.w - TEMPLATE_W; dx += 1) {
            const windowGray = new Uint8Array(TEMPLATE_W * TEMPLATE_H);
            const windowEdge = new Float32Array(TEMPLATE_W * TEMPLATE_H);
            
            for (let wy = 0; wy < TEMPLATE_H; wy++) {
              for (let wx = 0; wx < TEMPLATE_W; wx++) {
                const srcIdx = (dy + wy) * TEMPLATE_SEARCH_REGION.w + (dx + wx);
                const winIdx = wy * TEMPLATE_W + wx;
                windowGray[winIdx] = sourceGray.data[srcIdx];
                windowEdge[winIdx] = sourceEdge[srcIdx];
              }
            }

            const wGrayScore = calculateMaskedNormalizedCorrelation(
              windowGray,
              templateData.gray.data,
              templateData.alphaMask
            );
            const wEdgeScore = calculateMaskedNormalizedCorrelation(
              windowEdge,
              templateData.edge,
              templateData.edgeMask
            );
            
            const currentScore = wGrayScore * 0.5 + wEdgeScore * 0.5;
            
            if (currentScore > bestScore) {
              bestScore = currentScore;
              bestGrayScore = wGrayScore;
              bestEdgeScore = wEdgeScore;
              bestX = dx;
              bestY = dy;
              
              bestSimilarPixels = countSimilarPixels(
                windowEdge,
                templateData.edge,
                templateData.edgeMask
              );
            }
          }
        }

        const score = bestScore;
        const grayScore = bestGrayScore;
        const edgeScore = bestEdgeScore;
        const similarPixels = bestSimilarPixels;
        const matched = score >= TEMPLATE_MATCH_THRESHOLD;
        const scoreValue = Math.max(0, Math.round(score * 1000));
        let sourceTemplateCheckActive = true;
        let sourceTemplateDetected = false;

        if (matched) {
          opponentOverlayCanvas = captureRegionCanvas(
            frameCtx.canvas,
            OPPONENT_OVERLAY_SOURCE_REGION,
          );
        }

        if (sourceTemplateActivationToken > lastSourceTemplateActivationTokenRef.current) {
          sourceTemplateArmed = true;
          sourceTemplateConsecutiveMatches = 0;
          allyStatusImageUrl = null;
          opponentStatusImageUrl = null;
          lastSourceTemplateActivationTokenRef.current = sourceTemplateActivationToken;
        }

        const sourceTemplateImageData = frameCtx.getImageData(
          SOURCE_TEMPLATE_REGION.x,
          SOURCE_TEMPLATE_REGION.y,
          SOURCE_TEMPLATE_REGION.w,
          SOURCE_TEMPLATE_REGION.h,
        );
        sourceTemplateScore = calculateTemplateScore(
          sourceTemplateImageData,
          sourceTemplateData,
        ).score;
        sourceTemplateDetected = sourceTemplateScore >= SOURCE_TEMPLATE_MATCH_THRESHOLD;

        if (sourceTemplateArmed) {
          sourceTemplateConsecutiveMatches = sourceTemplateDetected
            ? sourceTemplateConsecutiveMatches + 1
            : 0;

          if (sourceTemplateConsecutiveMatches >= SOURCE_TEMPLATE_REQUIRED_FRAMES) {
            allyStatusImageUrl = captureStackedRegions(frameCtx.canvas, ALLY_STATUS_REGIONS);
            opponentStatusImageUrl = captureStackedRegions(
              frameCtx.canvas,
              OPPONENT_STATUS_REGIONS,
            );
            if (opponentOverlayCanvas) {
              const opponentJpgBase64 = createOpponentCompositeJpgBase64(
                frameCtx.canvas,
                opponentOverlayCanvas,
              );
              if (opponentJpgBase64) {
                void callOpponentImageApi(opponentJpgBase64);
              }
            }
            sourceTemplateArmed = false;
          }
        }

        if (disposed) return;

        detectionTickRef.current += 1;
        const nextResult: DetectResult = {
          detectionTick: detectionTickRef.current,
          templateScorePermille: scoreValue,
          templateScorePercent: Math.max(0, Math.round(score * 100)),
          templateDetected: matched,
          sourceTemplateScorePermille: Math.max(0, Math.round(sourceTemplateScore * 1000)),
          sourceTemplateScorePercent: Math.max(0, Math.round(sourceTemplateScore * 100)),
          sourceTemplateCheckActive,
          sourceTemplateDetected,
          sourceTemplateConsecutiveMatches,
          allyStatusImageUrl,
          opponentStatusImageUrl,
          debug: {
            avgR: regionSummary.avgR,
            avgG: regionSummary.avgG,
            avgB: regionSummary.avgB,
            regionHeight: matched ? TEMPLATE_H : 0,
            templateThresholdPercent: Math.round(TEMPLATE_MATCH_THRESHOLD * 100),
            templateThresholdScore: TEMPLATE_MATCH_THRESHOLD,
            templateRegion: { x: TEMPLATE_SEARCH_REGION.x + bestX, y: TEMPLATE_SEARCH_REGION.y + bestY, w: TEMPLATE_W, h: TEMPLATE_H },
            sourceTemplateThresholdPercent: Math.round(SOURCE_TEMPLATE_MATCH_THRESHOLD * 100),
            sourceTemplateThresholdScore: SOURCE_TEMPLATE_MATCH_THRESHOLD,
            sourceTemplateRegion: SOURCE_TEMPLATE_REGION,
            templateLogs: [
              {
                index: 0,
                totalPixels: templateData.maskPixels,
                matchedPixels: similarPixels,
                matchRatio: score,
                grayScore,
                edgeScore,
                minR: regionSummary.avgR,
                maxR: regionSummary.avgR,
                minG: regionSummary.avgG,
                maxG: regionSummary.avgG,
                minB: regionSummary.avgB,
                maxB: regionSummary.avgB,
              },
            ],
          },
        };

        const signature = [
          nextResult.templateDetected ? 1 : 0,
          nextResult.templateScorePermille,
          nextResult.sourceTemplateScorePermille,
          nextResult.sourceTemplateDetected ? 1 : 0,
          nextResult.sourceTemplateConsecutiveMatches,
          nextResult.allyStatusImageUrl ?? '',
          nextResult.opponentStatusImageUrl ?? '',
          nextResult.debug.avgR,
          nextResult.debug.avgG,
          nextResult.debug.avgB,
        ].join('|');

        if (matched || lastSignatureRef.current !== signature) {
          lastSignatureRef.current = signature;
          setResult(nextResult);
        }
      } finally {
        detecting = false;
      }
    };

    const scheduleNextDetect = () => {
      if (disposed) return;
      timerRef.current = window.setTimeout(() => {
        detect();
        scheduleNextDetect();
      }, Math.max(1000, Math.round(1000 / fps)));
    };

    void Promise.all([loadTemplateImage(), loadSourceTemplateImage()])
      .then(([templateImage, sourceTemplateImage]) => {
        if (disposed) return;

        templateData = imageToTemplateData(templateImage);
        sourceTemplateData = imageToTemplateData(
          sourceTemplateImage,
          SOURCE_TEMPLATE_REGION.w,
          SOURCE_TEMPLATE_REGION.h,
        );
        detect();
        scheduleNextDetect();
      })
      .catch((error) => {
        console.error('[useBattleColorDetect] 模板匹配初始化失败', error);
        if (!disposed) {
          setResult(DEFAULT_RESULT);
        }
      });

    return () => {
      disposed = true;
      cleanupTimer();
    };
  }, [videoRef, stream, fps, cropRect, sourceTemplateActivationToken]);

  return stream ? result : DEFAULT_RESULT;
}
